import clsx from 'clsx';
import Card from 'components/atoms/Card/Card';
import Notice from 'components/atoms/Notice/Notice';
import PaypalError from 'components/molecules/PaypalError/PaypalError';
import UserBalance from 'components/organisms/Header/UserBalance';
import TransactionHistory from 'components/organisms/TransactionHistory/TransactionHistory';
import { EGIFTCARDREWARDS, MOBILETOPUPREWARDS, PAYPALREWARDS } from 'components/pages/pages';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { RewardsQuery } from './__generated__/RewardsQuery.graphql';
import EGiftCardsRewardsMx from './assets/EGiftCardsRewardsMx.svg';
import MobileRewardsMx from './assets/MobileRewardsMx.svg';
import PayPalRewardsMx from './assets/PayPalRewardsMx.svg';
import classes from './Rewards.module.scss';
import RewardsLinkCard from './RewardsLinkCard';

export interface Props {}

const query = graphql`
  query RewardsQuery {
    ...TransactionHistoryFragment
    walletBalance {
      id
      ...CurrentEarningsCard_currentEarnings
    }
    targetRedemptionValue {
      ...UserBalance_target
    }
    redemptionTypes {
      types {
        id
        name
      }
    }
  }
`;

const Rewards: React.FC<Props> = () => {
  const router = useRouter();
  const data = useLazyLoadQuery<RewardsQuery>(
    query,
    {},
    {
      fetchPolicy: 'store-and-network',
    },
  );

  const cashoutErrorNotice = (
    <Notice
      message="We are having problems loading redemptions at the moment. Please try again later."
      status="warning"
    />
  );

  const balanceErrorNotice = (
    <Notice
      message="We are having problems loading your balance at the moment. Please try again later."
      status="warning"
    />
  );

  const redemptionTypes = data.redemptionTypes?.types.map((rt) => rt.name);

  async function handlePaypalErrorClose() {
    await router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, redemptionsError: undefined },
      },
      router.asPath.split('?')[0],
      {
        shallow: true,
      },
    );
  }

  return (
    <div className={clsx(classes.root)}>
      {Boolean(router.query.redemptionsError) && router.query.redemptionsError !== '' && (
        <PaypalError onClose={handlePaypalErrorClose} className={classes.paypalError} />
      )}
      <div className={classes.balanceContainer}>
        <UserBalance isDescriptionVisible className={classes.balanceStyling} />
      </div>
      <div className={classes.cardLinkContainer}>
        {redemptionTypes && redemptionTypes.includes('MOBILE') && (
          <RewardsLinkCard
            icon={MobileRewardsMx}
            title="Mobile Top Up"
            description="Send credit direct to your phone"
            href={MOBILETOPUPREWARDS.path}
          />
        )}

        {redemptionTypes && redemptionTypes?.includes('PAYPAL') && (
          <RewardsLinkCard
            icon={PayPalRewardsMx}
            title="PayPal"
            description="Cash out direct to your PayPal"
            href={PAYPALREWARDS.path}
          />
        )}

        {redemptionTypes && redemptionTypes?.includes('GIFT_CARD') && (
          <RewardsLinkCard
            icon={EGiftCardsRewardsMx}
            title="E-Gift Cards"
            description="Choose from many big brands"
            href={EGIFTCARDREWARDS.path}
          />
        )}
      </div>
      <Card>
        <TransactionHistory isCollapsible transactionsRef={data} />
      </Card>
    </div>
  );
};

export default Rewards;
